import { Flex } from 'components';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'components/_form';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { editPriorityLevel, getPriorityLevels } from 'api/priorityLevels';
import { IPriorityLevel } from 'types/priorityLevel';
import { AddPriorityLevelForm } from './components/Form/AddPriorityLevelForm/AddPriorityLevelForm';
import { EditPriorityLevelForm } from './components/Form/EditPriorityLevelForm/EditPriorityLevelForm';
import { PrioritiesTable } from './components/PrioritiesTable/PrioritiesTable';
import { toast } from 'react-toastify';

export const Priorities = () => {
  const [isAddFormOpen, setAddIsFormOpen] = useState(false);
  const [priorityLevels, setPriorityLevels] = useState<IPriorityLevel[]>([]);
  const [editingPriorityLevelId, setEditingPriorityLevelId] =
    useState<number>();

  const { t } = useTranslation();

  const handleGetPriorityLevels = async () => {
    const priorityLevelsRes = await getPriorityLevels();

    if ('error' in priorityLevelsRes) {
      return;
    }

    setPriorityLevels(priorityLevelsRes.data);
  };

  const handleChangePriorityLevelOrder = async (
    priorityLevel: IPriorityLevel,
    type: 'increase' | 'decrease'
  ) => {
    const sorted = [...priorityLevels].sort(
      (a, b) => (a.order || 0) - (b.order || 0) || a.id - b.id
    );
    const currentIndex = sorted.findIndex(({ id }) => id === priorityLevel.id);
    const swapIndex = type === 'increase' ? currentIndex + 1 : currentIndex - 1;
    const swapWith = sorted[swapIndex];

    if (!swapWith) {
      return;
    }

    const editRes = await editPriorityLevel(priorityLevel.id, {
      priority_level: { order: swapWith.order }
    });

    if ('error' in editRes) {
      toast.error(editRes.error.message);
      return;
    }

    const swapRes = await editPriorityLevel(swapWith.id, {
      priority_level: { order: priorityLevel.order }
    });

    if ('error' in swapRes) {
      toast.error(swapRes.error.message);
      return;
    }

    handleGetPriorityLevels();
  };

  useEffect(() => {
    handleGetPriorityLevels();
  }, []);

  return (
    <>
      <Flex justifyContent="flex-end" width="100%" style={{ gap: '10px' }}>
        <Button
          variant="eucalyptus"
          onClick={() => {
            setAddIsFormOpen(true);
          }}
        >
          <FontAwesomeIcon icon={faPlusCircle} /> {t('buttons.add')}
        </Button>
      </Flex>

      <PrioritiesTable
        priorityLevels={priorityLevels}
        onEdit={(priorityLevel) => setEditingPriorityLevelId(priorityLevel.id)}
        onChangeOrder={handleChangePriorityLevelOrder}
      />

      <AddPriorityLevelForm
        isOpen={isAddFormOpen}
        priorityLevelsCount={priorityLevels.length}
        onCancelClick={() => {
          setAddIsFormOpen(false);
        }}
        onSuccess={(newPriorityLevel) => {
          setPriorityLevels((prev) => [...prev, newPriorityLevel]);
          setAddIsFormOpen(false);
        }}
      />

      {editingPriorityLevelId && (
        <EditPriorityLevelForm
          isOpen={!!editingPriorityLevelId}
          editingPriorityLevelId={editingPriorityLevelId}
          onCancelClick={() => {
            setEditingPriorityLevelId(undefined);
          }}
          onSubmitSuccess={() => {
            handleGetPriorityLevels();
            setEditingPriorityLevelId(undefined);
          }}
          onDeleteSuccess={() => {
            handleGetPriorityLevels();
            setEditingPriorityLevelId(undefined);
          }}
        />
      )}
    </>
  );
};
